import { createContext, useState } from "react";

const AuthContext = createContext();

// Helper function to safely parse JSON
const safelyParseJSON = (jsonString, defaultValue) => {
  if (!jsonString) return defaultValue;
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('Error parsing JSON:', error);
    return defaultValue;
  }
};

export const AuthContextProvider = (props) => {
  // Simulate an error with JSON.parse(undefined)
  // This will trigger the error but our global error handler should catch it
  // To activate the test, set simulateError to true
  // const simulateError = true; // Set to true to simulate the error
  
  // if (simulateError) {
  //   // This will cause "SyntaxError: undefined is not valid JSON"
  //   // The global handler should catch it
  //   console.log("Simulating JSON.parse undefined error...");
  //   JSON.parse(undefined);
  // }

  const [email, setEmail] = useState(localStorage.getItem("email"));
  const [firstName, setFirstName] = useState(localStorage.getItem("firstName"));
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [tokenExpirationTime, setTokenExpirationTime] = useState(
    localStorage.getItem("tokenExpirationTime")
  );
  
  // Safely parse authLocations
  const [authLocations, setAuthLocations] = useState(() => {
    const savedLocations = localStorage.getItem("authLocations");
    return safelyParseJSON(savedLocations, null);
  });
  
  const [doorCountingEnabled, setDoorCountingEnabled] = useState(localStorage.getItem("doorCountingEnabled"));
  const [dashboardEnabled, setDashboardEnabled] = useState(localStorage.getItem("dashboardEnabled"));
  
  // Safely parse userZonePreferences
  const [userZonePreferences, setUserZonePreferences] = useState(() => {
    const savedPreferences = localStorage.getItem("userZonePreferences");
    return safelyParseJSON(savedPreferences, {});
  });
  
  // Safely parse authLocationData
  const [authLocationData, setAuthLocationData] = useState(() => {
    const savedData = localStorage.getItem("authLocationData");
    return safelyParseJSON(savedData, null);
  });
  
  const [venue, setVenue] = useState(localStorage.getItem("venue"));
  const [league, setLeague] = useState(localStorage.getItem("league"));
  const [dashboardVersion, setDashboardVersion] = useState(localStorage.getItem("dashboardVersion"));

  const login = (
    email,
    firstName,
    token,
    tokenExpirationTime,
    authLocations,
    doorCountingEnabled,
    dashboardEnabled,
    userZonePreferences,
    authLocationData,
    venue,
    league,
    dashboardVersion,
  ) => {
    try {
      setEmail(email);
      setFirstName(firstName);
      setToken(token);
      setTokenExpirationTime(tokenExpirationTime);
      setAuthLocations(authLocations);
      setDoorCountingEnabled(doorCountingEnabled);
      setDashboardEnabled(dashboardEnabled);
      setUserZonePreferences(userZonePreferences || {});
      setAuthLocationData(authLocationData);
      setVenue(venue);
      setLeague(league);
      setDashboardVersion(dashboardVersion);
      
      localStorage.setItem("email", email);
      localStorage.setItem("firstName", firstName);
      localStorage.setItem("token", token);
      localStorage.setItem("tokenExpirationTime", tokenExpirationTime);
      localStorage.setItem("authLocations", JSON.stringify(authLocations));
      localStorage.setItem("doorCountingEnabled", doorCountingEnabled);
      localStorage.setItem("dashboardEnabled", dashboardEnabled);
      localStorage.setItem("userZonePreferences", JSON.stringify(userZonePreferences || {}));
      localStorage.setItem("authLocationData", JSON.stringify(authLocationData));
      localStorage.setItem("venue", venue);
      localStorage.setItem("league", league);
      localStorage.setItem("dashboardVersion", dashboardVersion);
    } catch (error) {
      console.error("Error during login:", error);
      // If something goes wrong during login, logout to clean state
      logout();
    }
  };

  // Updates the users preferences
  const updateUserZonePreferences = (preferences) => {
    try {
      setUserZonePreferences(preferences);
      localStorage.setItem("userZonePreferences", JSON.stringify(preferences));
    } catch (error) {
      console.error("Error updating user preferences:", error);
    }
  };

  const logout = () => {
    setEmail(null);
    setFirstName(null);
    setToken(null);
    setTokenExpirationTime(null);
    setAuthLocations(null);
    setDoorCountingEnabled(null);
    setDashboardEnabled(null);
    setUserZonePreferences({});
    setAuthLocationData(null);
    setVenue(null);
    setLeague(null);
    setDashboardVersion(null);
    // Full clear insurance
    localStorage.clear();
  };

  const loggedIn = !!email;

  const updateAuthLocations = (newAuthLocations) => {
    try {
      setAuthLocations(newAuthLocations);
      localStorage.setItem("authLocations", JSON.stringify(newAuthLocations));
    } catch (error) {
      console.error("Error updating auth locations:", error);
    }
  };
  
  const updateAuthLocationData = (newAuthLocationData) => {
    try {
      setAuthLocationData(newAuthLocationData);
      localStorage.setItem("authLocationData", JSON.stringify(newAuthLocationData));
    } catch (error) {
      console.error("Error updating auth location data:", error);
    }
  };
  
  const updateDashboardEnabled = (newDashboardEnabled) => {
    try {
      setDashboardEnabled(newDashboardEnabled);
      localStorage.setItem("dashboardEnabled", newDashboardEnabled);
    } catch (error) {
      console.error("Error updating dashboard enabled:", error);
    }
  };

  const contextValue = {
    email: email,
    firstName: firstName,
    token: token,
    tokenExpirationTime: tokenExpirationTime,
    authLocations: authLocations,
    dashboardEnabled: dashboardEnabled,
    doorCountingEnabled: doorCountingEnabled,
    userZonePreferences: userZonePreferences,
    authLocationData: authLocationData,
    venue: venue,
    league: league,
    dashboardVersion: dashboardVersion,
    login: login,
    logout: logout,
    updateUserZonePreferences: updateUserZonePreferences,
    updateAuthLocations: updateAuthLocations,
    updateAuthLocationData: updateAuthLocationData,
    updateDashboardEnabled: updateDashboardEnabled,
    loggedIn: loggedIn,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {props.children}
    </AuthContext.Provider>
  );
};

export default AuthContext;